// packages/shared/api-client/endpoints/leadership.js
// Leadership directory and position management API endpoints

import { baseApi } from "../baseApi";

/**
 * Leadership API endpoints
 * Provides leadership directory, position management, and history tracking
 */
const leadershipApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all leadership positions (defaults to current leadership)
    getLeadership: builder.query({
      query: (params = {}) => ({
        url: '/api/leadership',
        params, // { includeHistory?, title?, userId?, vacant?, startDate?, endDate? }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Leadership', id })),
              { type: 'Leadership', id: 'LIST' },
            ]
          : [{ type: 'Leadership', id: 'LIST' }],
    }),

    // Get leadership history
    getLeadershipHistory: builder.query({
      query: (params = {}) => ({
        url: '/api/leadership/history',
        params, // { title?, userId?, startDate?, endDate? }
      }),
      providesTags: ['Leadership'],
    }),

    // Get vacant leadership positions
    getVacantPositions: builder.query({
      query: () => '/api/leadership/vacant',
      providesTags: ['Leadership'],
    }),

    // Get specific leadership position by ID
    getLeadershipById: builder.query({
      query: (id) => `/api/leadership/${id}`,
      providesTags: (result, error, id) => [{ type: 'Leadership', id }],
    }),

    // Admin: Create new leadership position
    createLeadershipPosition: builder.mutation({
      query: (positionData) => ({
        url: '/api/leadership',
        method: 'POST',
        body: positionData, // { title, userId?, termStart?, termEnd?, displayOrder? }
      }),
      invalidatesTags: [{ type: 'Leadership', id: 'LIST' }],
    }),

    // Admin: Update leadership position
    updateLeadershipPosition: builder.mutation({
      query: ({ id, ...positionData }) => ({
        url: `/api/leadership/${id}`,
        method: 'PUT',
        body: positionData, // { title?, userId?, termStart?, termEnd?, displayOrder? }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Leadership', id },
        { type: 'Leadership', id: 'LIST' },
      ],
    }),

    // Admin: Vacate a leadership position (remove user assignment)
    vacatePosition: builder.mutation({
      query: (id) => ({
        url: `/api/leadership/${id}/vacate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Leadership', id },
        { type: 'Leadership', id: 'LIST' },
      ],
    }),

    // Admin: Delete leadership position
    deleteLeadershipPosition: builder.mutation({
      query: (id) => ({
        url: `/api/leadership/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Leadership', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in components
export const useGetLeadershipQuery = leadershipApi.useGetLeadershipQuery;
export const useGetLeadershipHistoryQuery = leadershipApi.useGetLeadershipHistoryQuery;
export const useGetVacantPositionsQuery = leadershipApi.useGetVacantPositionsQuery;
export const useGetLeadershipByIdQuery = leadershipApi.useGetLeadershipByIdQuery;
export const useCreateLeadershipPositionMutation = leadershipApi.useCreateLeadershipPositionMutation;
export const useUpdateLeadershipPositionMutation = leadershipApi.useUpdateLeadershipPositionMutation;
export const useVacatePositionMutation = leadershipApi.useVacatePositionMutation;
export const useDeleteLeadershipPositionMutation = leadershipApi.useDeleteLeadershipPositionMutation;
